import { Lightbulb, ArrowRight, Search, Store } from "lucide-react";

/**
 * v12 · Cuando una búsqueda sale vacía: por qué ha pasado y qué búsqueda probar.
 * «Probar esta búsqueda» rellena el formulario; el cliente la revisa y la lanza (no se lanza sola).
 */
export default function EmptyAdvice({ advice, onTry, onRecommenders, recommendersBusy, disabled }) {
  const sug = advice?.suggestion;
  const n = advice?.numbers || {};
  return (
    <div className="rounded-lg border border-brand-accent/40 bg-brand-panel p-6" data-testid="opp-empty-advice">
      <p className="text-base font-bold text-brand-ink">No hemos encontrado oportunidades en esta búsqueda</p>
      <p className="mt-1 text-xs text-brand-muted">No se descuenta de tu plan.</p>

      <div className="mt-4 space-y-2 text-sm">
        <p className="text-brand-ink"><span className="font-semibold">Qué ha pasado:</span> {advice.reason}</p>
        {(n.encontradas > 0 || n.analizadas > 0) && (
          <p className="text-xs text-brand-muted">
            {n.encontradas} cuentas encontradas · {n.analizadas} analizadas a fondo · {n.con_encaje || 0} con encaje
            {n.fuera_de_tu_zona > 0 ? ` · ${n.fuera_de_tu_zona} fuera de tu zona` : ""}
          </p>
        )}
        {advice.top_reasons?.length > 0 && (
          <p className="text-xs text-brand-muted">
            Motivos más repetidos: {advice.top_reasons.map((r) => `${r.reason} (${r.count})`).join(" · ")}
          </p>
        )}
        {advice.tried?.length > 0 && (
          <p className="text-xs text-brand-muted" data-testid="opp-empty-tried">
            Antes de rendirnos probamos también: {advice.tried.join(" · ")}.
          </p>
        )}
        <p className="flex gap-2 text-brand-ink"><Lightbulb size={16} className="mt-0.5 shrink-0 text-brand-accent" /> {advice.advice}</p>
      </div>

      {advice.local_recommenders && onRecommenders && (
        <div className="mt-5 rounded-md border border-brand-accent/40 bg-brand-accent/[0.05] p-4" data-testid="opp-local-recommenders">
          <p className="flex items-center gap-2 text-sm font-semibold text-brand-ink"><Store size={16} className="text-brand-accent" /> En un negocio local, lo que mejor funciona en Instagram</p>
          <p className="mt-1.5 text-xs text-brand-muted">
            Casi nadie pone su barrio o su ciudad en la bio, así que encontrar clientes vecinos uno a uno es difícil.
            Busca negocios y profesionales de tu zona que te puedan mandar clientes: a una peluquería, fotógrafos de bodas
            o tiendas de novia; a un gimnasio, fisioterapeutas o nutricionistas. Ellos sí ponen su ciudad en la bio.
          </p>
          <button type="button" onClick={onRecommenders} disabled={disabled || recommendersBusy} data-testid="opp-try-recommenders"
            className="mt-3 inline-flex items-center gap-2 rounded-md border border-brand-accent px-4 py-2 text-sm font-bold text-brand-accent hover:bg-brand-accent/10 disabled:opacity-60">
            <Search size={15} /> {recommendersBusy ? "Preparando perfiles…" : "Buscar recomendadores de mi zona"}
          </button>
        </div>
      )}

      {sug ? (
        <div className="mt-5 rounded-md border border-brand-line bg-brand-canvas/40 p-4" data-testid="opp-empty-suggestion">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">Te proponemos esta búsqueda</p>
          <dl className="mt-2 grid gap-1.5 text-sm sm:grid-cols-[150px_1fr]">
            {sug.profile && (<><dt className="text-brand-muted">Perfil</dt><dd className="text-brand-ink">{sug.profile}</dd></>)}
            <dt className="text-brand-muted">Palabras</dt><dd className="text-brand-ink">{(sug.keywords || []).join(", ") || "—"}</dd>
            <dt className="text-brand-muted">Ubicación</dt><dd className="text-brand-ink">{sug.location || "Sin ubicación"}</dd>
            {sug.reference_accounts?.length > 0 && (<><dt className="text-brand-muted">Cuentas parecidas</dt><dd className="text-brand-ink">{sug.reference_accounts.join(", ")}</dd></>)}
            {sug.exclusions?.length > 0 && (<><dt className="text-brand-muted">Excluir</dt><dd className="text-brand-ink">{sug.exclusions.join(", ")}</dd></>)}
          </dl>
          {sug.why && <p className="mt-2 text-xs text-brand-muted">{sug.why}</p>}
          <button type="button" onClick={() => onTry(sug)} disabled={disabled} data-testid="opp-try-suggestion"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-brand-accent px-4 py-2.5 text-sm font-bold text-brand-canvas hover:brightness-110 disabled:opacity-60">
            <Search size={15} /> Probar esta búsqueda <ArrowRight size={15} />
          </button>
        </div>
      ) : (
        <ul className="mt-4 list-disc space-y-1 pl-5 text-xs text-brand-muted">
          {(advice.tips || []).slice(1).map((t) => <li key={t}>{t}</li>)}
        </ul>
      )}
    </div>
  );
}
